/**
 * Poseidon dashboard — sortable table state.
 *
 * Tables in this dashboard sort client-side over the rows the daemon already
 * returned. Sorting never refetches, so the operator's sort order cannot be
 * mistaken for a different data window.
 */
import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<TKey extends string> {
  key: TKey | null;
  direction: SortDirection;
}

export interface SortableTable<TKey extends string> {
  sort: SortState<TKey>;
  /** Cycles none → asc → desc → none for the clicked column. */
  toggle: (key: TKey) => void;
  setSort: (next: SortState<TKey>) => void;
  ariaSort: (key: TKey) => 'ascending' | 'descending' | 'none';
  indicator: (key: TKey) => string;
}

export function useSortableTable<TKey extends string>(
  initial: SortState<TKey> = { key: null, direction: 'desc' },
): SortableTable<TKey> {
  const [sort, setSort] = useState<SortState<TKey>>(initial);

  const toggle = useCallback((key: TKey) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'desc' };
    });
  }, []);

  const ariaSort = useCallback(
    (key: TKey): 'ascending' | 'descending' | 'none' => {
      if (sort.key !== key) return 'none';
      return sort.direction === 'asc' ? 'ascending' : 'descending';
    },
    [sort],
  );

  const indicator = useCallback(
    (key: TKey): string => {
      if (sort.key !== key) return '';
      return sort.direction === 'asc' ? '▲' : '▼';
    },
    [sort],
  );

  return { sort, toggle, setSort, ariaSort, indicator };
}

type Comparable = string | number | boolean | null | undefined;

function compareValues(a: Comparable, b: Comparable): number {
  if (a === b) return 0;
  // Missing values sort last regardless of direction, so an empty cell never
  // masquerades as the smallest value.
  if (a === null || a === undefined || a === '') return 1;
  if (b === null || b === undefined || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Stable sort of rows by the active column. `accessors` maps a column key to the
 * value that should be compared for that row; columns without an accessor are
 * simply not sortable, so the record is partial by design.
 */
export function useSortedRows<TRow, TKey extends string>(
  rows: readonly TRow[],
  sort: SortState<TKey>,
  accessors: Partial<Record<TKey, (row: TRow) => Comparable>>,
): TRow[] {
  return useMemo(() => {
    if (sort.key === null) return [...rows];
    const accessor = accessors[sort.key];
    if (accessor === undefined) return [...rows];
    const factor = sort.direction === 'asc' ? 1 : -1;
    return rows
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const result = compareValues(accessor(left.row), accessor(right.row));
        if (result === 0) return left.index - right.index;
        return result * factor;
      })
      .map((entry) => entry.row);
  }, [rows, sort, accessors]);
}
